/**
 * "Contracts" section for Customer Detail.
 * Lists every contract-related document for the customer in one stack:
 *   - Contract Packs (db.contract_packs)
 *   - Contract Variations (customer.documents[doc_tag=variation_of_contract])
 *   - Letters of Authorisation (customer.documents[doc_tag=letter_of_authority])
 *
 * Powered by `GET /api/customers/{id}/contract-docs` which normalises all
 * three into a unified `{kind, id, label, status, ...}` shape.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  FileSignature, Eye, Trash2, Loader2, CheckCircle2, Copy, Send, Mail, FilePen, Search, Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Textarea } from './ui/textarea';
import { SignaturePad } from './SignaturePad';

// Status meta per (kind, status) — colour-coded so the admin can scan a long
// list and spot what's awaiting them vs the customer vs counter-sign.
const STATUS_META = {
  // contract_pack
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  sent: { label: 'Awaiting Customer', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  customer_signed: { label: 'Customer Signed — Needs You', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  fully_signed: { label: 'Fully Signed', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  voided: { label: 'Voided', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  // variation / loa
  generated: { label: 'Generated', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  signed: { label: 'Signed', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const KIND_META = {
  contract_pack: { icon: FileSignature, accent: 'text-violet-600', label: 'Contract' },
  variation: { icon: FileSignature, accent: 'text-blue-600', label: 'Variation' },
  loa: { icon: FilePen, accent: 'text-emerald-700', label: 'LoA' },
};

export const ContractPacksSection = ({ customerId, onChanged }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counterSignPack, setCounterSignPack] = useState(null);
  const [signature, setSignature] = useState('');
  const [signing, setSigning] = useState(false);
  const [query, setQuery] = useState('');
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/contract-docs`);
      setDocs(data || []);
    } catch (e) {
      // silent — admin sees an empty card and an error toast on actions
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (customerId) reload(); /* eslint-disable-next-line */ }, [customerId]);

  const filtered = useMemo(() => {
    if (!query.trim()) return docs;
    const q = query.toLowerCase();
    return docs.filter((d) =>
      [d.label, d.filename, d.status, d.kind].filter(Boolean).join(' ').toLowerCase().includes(q),
    );
  }, [docs, query]);

  // ---- Per-kind actions --------------------------------------------------
  const previewContractPack = async (packId) => {
    try {
      const resp = await api.get(`/customers/${customerId}/contract-pack/${packId}/preview.pdf`, {
        responseType: 'blob',
      });
      window.open(URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
    } catch (e) {
      const detail = e.response?.data instanceof Blob
        ? await e.response.data.text().catch(() => '')
        : (e.response?.data?.detail || e.message || 'Preview failed');
      toast.error(String(detail).slice(0, 200));
    }
  };

  const previewDoc = async (docId) => {
    try {
      const resp = await api.get(`/customers/${customerId}/documents/${docId}/preview`, { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    }
  };

  const remove = async (packId) => {
    if (!window.confirm('Delete this draft contract pack?')) return;
    try {
      await api.delete(`/customers/${customerId}/contract-pack/${packId}`);
      toast.success('Deleted');
      reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const voidContract = async () => {
    const reason = voidReason.trim();
    if (!reason) { toast.error('Please enter a reason for voiding'); return; }
    setVoiding(true);
    try {
      await api.post(`/customers/${customerId}/contract-pack/${voidTarget.id}/void`, { reason });
      toast.success('Contract voided — you can now raise a new one via the wizard');
      setVoidTarget(null);
      setVoidReason('');
      reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Void failed');
    } finally {
      setVoiding(false);
    }
  };

  const resend = async (packId) => {
    try {
      const { data } = await api.post(`/customers/${customerId}/contract-pack/${packId}/send`, { send_email: true });
      toast.success('Sign link re-sent to customer');
      navigator.clipboard?.writeText(data.sign_link).catch(() => {});
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    }
  };

  const emailPack = async (packId) => {
    if (!window.confirm('Send the full Contract Pack PDF to the customer via email?')) return;
    try {
      const { data } = await api.post(`/customers/${customerId}/contract-pack/${packId}/email`, {});
      toast.success(`Pack emailed to ${data.to}`);
      reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email failed');
    }
  };

  const counterSign = async () => {
    if (!signature) { toast.error('Please sign in the box above first'); return; }
    setSigning(true);
    try {
      await api.post(`/customers/${customerId}/contract-pack/${counterSignPack.id}/admin-sign`, {
        signature_data_url: signature,
      });
      toast.success('Contract fully executed — copy emailed to customer');
      setCounterSignPack(null);
      setSignature('');
      reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Counter-sign failed');
    } finally {
      setSigning(false);
    }
  };

  const copySignLink = (token) => {
    const url = `${window.location.origin}/sign-variation/${token}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success('Sign link copied to clipboard'),
      () => toast.error('Could not copy — please copy from the share-link panel'),
    );
  };

  const sendVariationReminder = async (docId) => {
    try {
      await api.post(`/customers/${customerId}/sign-doc/${docId}/send-reminder`, {});
      toast.success('Reminder email sent');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reminder failed');
    }
  };

  if (loading) {
    return (
      <Card data-testid="contract-packs-section-loading">
        <CardContent className="py-6 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (!docs.length) return null;

  return (
    <>
      <Card data-testid="contract-packs-section">
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-violet-600" />
            Contracts, Variations &amp; LoAs ({docs.length})
          </CardTitle>
          {docs.length > 3 && (
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="h-7 w-40 pl-7 text-xs"
                data-testid="contract-docs-search"
              />
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-2">
          {filtered.length === 0 ? (
            <p className="text-xs text-slate-400 py-2">No matches for &ldquo;{query}&rdquo;.</p>
          ) : filtered.map((d) => {
            const kindMeta = KIND_META[d.kind] || KIND_META.contract_pack;
            const meta = STATUS_META[d.status] || STATUS_META.draft;
            const KindIcon = kindMeta.icon;
            return (
              <div
                key={`${d.kind}-${d.id}`}
                className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded border border-slate-100 hover:bg-slate-50"
                data-testid={`contract-doc-${d.kind}-${d.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <KindIcon className={`w-4 h-4 ${kindMeta.accent}`} />
                    <span className="font-medium text-sm text-slate-800">{d.label}</span>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide">{kindMeta.label}</Badge>
                    <Badge className={`${meta.cls} border text-[10px]`}>{meta.label}</Badge>
                    {d.bundled_loa_doc_id && (
                      <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">
                        + LoA bundled
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Created {d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB') : '—'}
                    {d.sent_at ? ` · Sent ${new Date(d.sent_at).toLocaleDateString('en-GB')}` : ''}
                    {d.customer_signed_at ? ` · Customer signed ${new Date(d.customer_signed_at).toLocaleDateString('en-GB')}` : ''}
                    {d.signed_at && !d.customer_signed_at ? ` · Signed ${new Date(d.signed_at).toLocaleDateString('en-GB')}` : ''}
                  </p>
                  {d.status === 'voided' && d.void_reason && (
                    <p className="text-[11px] text-rose-500 mt-0.5" data-testid={`pack-void-reason-${d.id}`}>
                      Voided{d.voided_by ? ` by ${d.voided_by}` : ''} — {d.void_reason}
                    </p>
                  )}
                </div>
                <div className="flex gap-1 flex-wrap">
                  {d.kind === 'contract_pack' ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => previewContractPack(d.id)} className="h-8 text-xs" data-testid={`pack-preview-${d.id}`}>
                        <Eye className="w-3 h-3 mr-1" /> Preview
                      </Button>
                      {d.status !== 'draft' && (
                        <Button size="sm" variant="ghost" onClick={() => emailPack(d.id)} className="h-8 text-xs text-sky-700 hover:bg-sky-50" data-testid={`pack-email-${d.id}`}>
                          <Mail className="w-3 h-3 mr-1" /> Email Pack
                        </Button>
                      )}
                      {d.status === 'sent' && (
                        <Button size="sm" variant="ghost" onClick={() => resend(d.id)} className="h-8 text-xs" data-testid={`pack-resend-${d.id}`}>
                          <Send className="w-3 h-3 mr-1" /> Re-send Link
                        </Button>
                      )}
                      {d.status === 'customer_signed' && (
                        <Button
                          size="sm"
                          onClick={() => setCounterSignPack(d)}
                          className="h-8 text-xs bg-blue-600 hover:bg-blue-700"
                          data-testid={`pack-counter-sign-${d.id}`}
                        >
                          <FileSignature className="w-3 h-3 mr-1" /> Counter-sign
                        </Button>
                      )}
                      {d.status === 'draft' && (
                        <Button size="sm" variant="ghost" onClick={() => remove(d.id)} className="h-8 text-xs text-rose-600 hover:bg-rose-50" data-testid={`pack-delete-${d.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                      {d.status !== 'draft' && d.status !== 'voided' && (
                        <Button size="sm" variant="ghost" onClick={() => { setVoidTarget(d); setVoidReason(''); }} className="h-8 text-xs text-rose-600 hover:bg-rose-50" data-testid={`pack-void-${d.id}`}>
                          <Ban className="w-3 h-3 mr-1" /> Void
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => previewDoc(d.id)} className="h-8 text-xs" data-testid={`doc-preview-${d.id}`}>
                        <Eye className="w-3 h-3 mr-1" /> Preview
                      </Button>
                      {d.sign_token && d.status === 'sent' && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => copySignLink(d.sign_token)} className="h-8 text-xs" data-testid={`doc-copy-link-${d.id}`}>
                            <Copy className="w-3 h-3 mr-1" /> Copy Link
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => sendVariationReminder(d.id)} className="h-8 text-xs text-amber-700 hover:bg-amber-50" data-testid={`doc-remind-${d.id}`}>
                            <Mail className="w-3 h-3 mr-1" /> Remind
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Counter-sign dialog */}
      <Dialog open={!!counterSignPack} onOpenChange={(o) => { if (!o) { setCounterSignPack(null); setSignature(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="counter-sign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-blue-600" />
              Counter-sign Contract
            </DialogTitle>
            <DialogDescription>
              The customer has signed. Sign on behalf of Helios to fully execute the contract — a copy will be emailed to the customer automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-xs text-slate-600 font-medium block">Nick Lowther — sign below</label>
            <SignaturePad onChange={setSignature} height={150} dataTestId="counter-sign-pad" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setCounterSignPack(null); setSignature(''); }}>Cancel</Button>
            <Button
              onClick={counterSign}
              disabled={signing || !signature}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="counter-sign-submit"
            >
              {signing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Submit Signature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void contract dialog */}
      <Dialog open={!!voidTarget} onOpenChange={(o) => { if (!o) { setVoidTarget(null); setVoidReason(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="void-contract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Ban className="w-5 h-5" /> Void Contract
            </DialogTitle>
            <DialogDescription>
              This voids <strong>{voidTarget?.label}</strong>
              {voidTarget && STATUS_META[voidTarget.status] ? ` (${STATUS_META[voidTarget.status].label})` : ''}.
              It stays on file marked "Voided", the signing link is disabled, and you'll be able to
              raise a fresh contract via the wizard. A reason is required for the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-slate-600 font-medium block">Reason for voiding</label>
            <Textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Customer changed system spec / pricing error — reissuing contract"
              rows={3}
              data-testid="void-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setVoidTarget(null); setVoidReason(''); }}>Cancel</Button>
            <Button
              onClick={voidContract}
              disabled={voiding || !voidReason.trim()}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="void-contract-submit"
            >
              {voiding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
              Void Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContractPacksSection;
